import { useState, useEffect, createContext, useContext } from 'react';
import { apiService, handleApiError, configureApi } from '../config/api';
import { verifySession, logoutUser, getCurrentToken, hasValidToken } from '../config/Autenticacion';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userModules, setUserModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [requiresPasswordChange, setRequiresPasswordChange] = useState(false);

  useEffect(() => {
    checkAuthStatus();
    setupGlobalFetchInterceptor();
  }, []);

  // ===== INTERCEPTOR PARA TOKEN EXPIRADO =====
  const setupGlobalFetchInterceptor = () => {
    const originalFetch = window.fetch;
    
    window.fetch = async (...args) => {
      try {
        const response = await originalFetch(...args);
        
        if ((response.status === 401 || response.status === 403) && isAuthenticated) {
          setTimeout(() => {
            alert('Su sesión ha expirado. Por favor, inicie sesión nuevamente.');
          }, 100);
          
          await forceLogout();
          
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        }
        
        return response;
      } catch (error) {
        throw error;
      }
    };
  };

  // ===== LOGOUT FORZADO =====
  const forceLogout = async () => {
    try {
      configureApi.clearAuthToken();
      
      sessionStorage.removeItem('authToken');
      sessionStorage.removeItem('userData');
      sessionStorage.removeItem('userModules');
      sessionStorage.removeItem('tokenExpiration');
      
      setUser(null);
      setUserModules([]);
      setIsAuthenticated(false);
      setRequiresPasswordChange(false);
    } catch (error) {
      console.error('Error en forceLogout:', error);
    }
  };



 const checkAuthStatus = async () => {
  try {
    setLoading(true);
    
    const currentToken = getCurrentToken();
  
    
    if (!currentToken) {

      setIsAuthenticated(false);
      setUser(null);
      setUserModules([]);
      setLoading(false);
      return;
    }
    
   
    let portalSession = { success: false, needsAuth: true };
    
    try {

      portalSession = await verifySession();
      

    } catch (portalError) {
  
      setIsAuthenticated(false);
      setUser(null);
      setUserModules([]);
      setLoading(false);
      return;
    }

    // VALIDAR LA RESPUESTA DEL PORTAL
    if (!portalSession.success || !portalSession.user) {
     
      setIsAuthenticated(false);
      setUser(null);
      setUserModules([]);
      setLoading(false);
      return;
    }

  
    
    const userData = {
      id: portalSession.user.CodEmpleado || portalSession.user.id,
      nombre: portalSession.user.NombreEmpleado || portalSession.user.nombre || portalSession.user.usuario,
      usuario: portalSession.user.usuario || portalSession.user.Usuario,
      direccion: portalSession.user.Direccion || portalSession.user.direccion,
      teamsUserId: portalSession.user.TeamsUserId,
      esAdmin: portalSession.user.esAdmin || portalSession.user.isAdmin,
      isAdmin: portalSession.user.esAdmin || portalSession.user.isAdmin,
      email: portalSession.user.Email || portalSession.user.email || '',
      telefono: portalSession.user.Telefono || portalSession.user.telefono || ''
    };

    

    if (currentToken) {
      configureApi.setAuthToken(currentToken);
     
    }

    setUser(userData);

    if (userData.esAdmin || userData.isAdmin) {
      const allModules = getAllModules();
      setUserModules(allModules);
      sessionStorage.setItem('userModules', JSON.stringify(allModules));
    
    } else {
      const normalizedModules = processPortalPermissions(portalSession.user.permisos);
      setUserModules(normalizedModules);
      sessionStorage.setItem('userModules', JSON.stringify(normalizedModules));
   
    }

    setIsAuthenticated(true);
    setRequiresPasswordChange(false);
    setLoading(false);

    
  } catch (error) {

    setIsAuthenticated(false);
    setUser(null);
    setUserModules([]);
    setLoading(false);
  }
};


const processPortalPermissions = (permissions) => {
  if (!permissions) {
  
    return [];
  }

 

  // AGREGAR ESTOS LOGS:
  if (permissions.Aplicaciones) {
 
    permissions.Aplicaciones.forEach((app, index) => {
  
    });
  }

  let normalizedModules = [];

  if (permissions.Aplicaciones && Array.isArray(permissions.Aplicaciones)) {
    const gremialApp = permissions.Aplicaciones.find(app => 
      app.NombreAplicacion === "REPRESENTANTES GREMIALES"
    );
    
   
    
    if (gremialApp && gremialApp.Modulos) {
 
      normalizedModules = transformModules(gremialApp.Modulos);
    }
  }


  
  return normalizedModules;
};
  // ===== TRANSFORMAR MÓDULOS =====
  const transformModules = (modules) => {
    return modules.map(module => ({
      IdModulo: module.IdModulo,
      Descripcion: module.ModuloDescripcion || module.Descripcion || module.Modulo,
      SubModulos: (module.SubModulos || []).map(subModule => {
        let acciones = [];
        
        if (subModule.ACCION && Array.isArray(subModule.ACCION)) {
          acciones = subModule.ACCION;
        } else if (subModule.Acciones) {
          if (typeof subModule.Acciones === 'string') {
            acciones = subModule.Acciones.split(',').map(a => a.trim());
          } else if (Array.isArray(subModule.Acciones)) {
            acciones = subModule.Acciones;
          }
        }
        
        return {
          IdSubModulo: subModule.IdSubModulo,
          Descripcion: (subModule.SubModuloDescripcion || subModule.Descripcion || subModule.SubModulo || '').trim(),
          ACCION: acciones
        };
      })
    }));
  };

  const getAllModules = () => {
    return [
      {
        "IdModulo": 10,
        "Descripcion": "gremiales",
        "SubModulos": [
          {
            "IdSubModulo": 17,
            "Descripcion": "verificar",
            "ACCION": ["LISTAR", "AGREGAR", "MODIFICAR", "ELIMINAR"]
          },
          {
            "IdSubModulo": 18,
            "Descripcion": "reporteria",
            "ACCION": ["LISTAR", "AGREGAR", "MODIFICAR", "ELIMINAR"]
          },
          {
            "IdSubModulo": 20,
            "Descripcion": "registro",
            "ACCION": ["LISTAR", "AGREGAR", "MODIFICAR", "ELIMINAR"]
          },
          {
            "IdSubModulo": 22,
            "Descripcion": "EditarRepresentantes",
            "ACCION": ["LISTAR", "AGREGAR", "MODIFICAR", "ELIMINAR"]
          },
          {
            "IdSubModulo": 23,
            "Descripcion": "categoria",
            "ACCION": ["LISTAR", "AGREGAR", "MODIFICAR", "ELIMINAR"]
          },
          {
            "IdSubModulo": 24,
            "Descripcion": "registro-modular",
            "ACCION": ["LISTAR", "AGREGAR", "MODIFICAR", "ELIMINAR"]
          }
        ]
      }
    ];
  };

  // ===== LOGIN =====
  const login = async (credentials) => {
    try {
      setLoading(true);
      
      
      
      if (!credentials.username || !credentials.password) {
        return { 
          success: false, 
          error: 'Usuario y contraseña son requeridos' 
        };
      }

      const loginData = {
        Usuario: credentials.username.trim(),
        Contrasenia: credentials.password
      };
      
      const data = await apiService.auth.login(loginData);
      
      if (data.success === true && data.requiresPasswordChange === true) {
        const tempUserData = {
          usuario: data.usuario,
          needsPasswordChange: true
        };
        
        setUser(tempUserData);
        setRequiresPasswordChange(true);
        
        return { 
          success: true,
          requiresPasswordChange: true,
          usuario: data.usuario,
          message: data.message || 'Debe cambiar su contraseña antes de continuar',
          user: tempUserData
        };
      }
      
      if (data.success === true && data.token) {
        const userData = {
          id: data.empleado?.CodEmpleado,
          nombre: data.empleado?.NombreEmpleado || data.usuario,
          usuario: data.usuario,
          direccion: data.empleado?.Direccion,
          teamsUserId: data.empleado?.TeamsUserId,
          esAdmin: data.esAdmin,
          isAdmin: data.esAdmin,
          email: data.empleado?.Email || '',
          telefono: data.empleado?.Telefono || ''
        };
        
        configureApi.setAuthToken(data.token);
        
        sessionStorage.setItem('authToken', data.token);
        sessionStorage.setItem('userData', JSON.stringify(userData));
        
        if (data.expiresAt) {
          sessionStorage.setItem('tokenExpiration', data.expiresAt);
        } else if (data.expiresIn) {
          const expirationTime = Date.now() + (data.expiresIn * 1000);
          const expirationDate = new Date(expirationTime).toISOString();
          sessionStorage.setItem('tokenExpiration', expirationDate);
        } else {
          const expirationTime = Date.now() + (2 * 60 * 60 * 1000);
          const expirationDate = new Date(expirationTime).toISOString();
          sessionStorage.setItem('tokenExpiration', expirationDate);
        }
        
        setUser(userData);
        
        let normalizedModules = [];
        
        if (data.esAdmin) {
          const allModules = getAllModules();
          setUserModules(allModules);
          sessionStorage.setItem('userModules', JSON.stringify(allModules));
        } else {
          normalizedModules = processPortalPermissions(data.permisos);
          setUserModules(normalizedModules);
        }
        
        setIsAuthenticated(true);
        setRequiresPasswordChange(false);
        
        return { 
          success: true, 
          user: userData, 
          modules: normalizedModules || []
        };
      }
      
      let errorMessage = data.error || data.message || 'Usuario o contraseña incorrectos';
      
      return { 
        success: false, 
        error: errorMessage
      };
      
    } catch (error) {
      const errorMessage = handleApiError(error, 'Error de conexión con el servidor');
      
      return { 
        success: false, 
        error: errorMessage
      };
    } finally {
      setLoading(false);
    }
  };

const logout = async (callBackend = false) => { // Cambiar default a false
  try {

    logoutUser(); // Llamar directamente sin condiciones
    return;
  } catch (error) {
   
    await forceLogout();
  }
};



  // ===== CAMBIAR CONTRASEÑA =====
  const changePassword = async (currentPassword, newPassword) => {
    try {
      setLoading(true);
      if (!currentPassword || !newPassword) {
        return { success: false, error: 'Contraseña actual y nueva son requeridas' };
      }
      if (newPassword.length < 8) {
        return { success: false, error: 'La nueva contraseña debe tener al menos 8 caracteres' };
      }
      
      const changePasswordData = {
        Usuario: user.usuario,
        Contrasenia: currentPassword,
        NuevaContrasenia: newPassword,
        action: 'change-password'
      };
      
      try {
        const data = await apiService.auth.login(changePasswordData);

        if (data.error && !data.error.includes('cambiar su contraseña')) {
          return { 
            success: false, 
            error: data.error || data.message || 'Error al cambiar la contraseña' 
          };
        }
        
        const loginData = {
          Usuario: user.usuario,
          Contrasenia: newPassword
        };
        
        const loginResponse = await apiService.auth.login(loginData);
        
        if (loginResponse.token) {
          const userData = {
            id: loginResponse.empleado?.CodEmpleado,
            nombre: loginResponse.empleado?.NombreEmpleado || loginResponse.usuario,
            usuario: loginResponse.usuario,
            direccion: loginResponse.empleado?.Direccion,
            teamsUserId: loginResponse.empleado?.TeamsUserId,
            esAdmin: loginResponse.esAdmin,
            isAdmin: loginResponse.esAdmin,
            email: loginResponse.empleado?.Email || '',
            telefono: loginResponse.empleado?.Telefono || ''
          };
          
          configureApi.setAuthToken(loginResponse.token);
          
          sessionStorage.setItem('authToken', loginResponse.token);
          sessionStorage.setItem('userData', JSON.stringify(userData));
          
          if (loginResponse.expiresAt) {
            sessionStorage.setItem('tokenExpiration', loginResponse.expiresAt);
          } else {
            const expirationTime = Date.now() + (2 * 60 * 60 * 1000);
            const expirationDate = new Date(expirationTime).toISOString();
            sessionStorage.setItem('tokenExpiration', expirationDate);
          }
          
          setUser(userData);
          
          if (loginResponse.esAdmin) {
            const allModules = getAllModules();
            setUserModules(allModules);
            sessionStorage.setItem('userModules', JSON.stringify(allModules));
          } else {
            const userModules = loginResponse.permisos?.Modulo || [];
            setUserModules(userModules);
            sessionStorage.setItem('userModules', JSON.stringify(userModules));
          }
          
          setRequiresPasswordChange(false);
          setIsAuthenticated(true);
          
          return { 
            success: true, 
            message: 'Contraseña cambiada con éxito. Bienvenido al sistema.' 
          };
        } else {
          return { success: false, error: 'Error al autenticar con la nueva contraseña' };
        }
      } catch (loginError) {
        const errorMessage = handleApiError(loginError, 'Error al cambiar la contraseña');
        return { success: false, error: errorMessage };
      }
    } catch (error) {
      const errorMessage = handleApiError(error, 'Error de conexión con el servidor');
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // ===== FUNCIONES DE PERMISOS =====
  const getAvailableSubModules = () => {
    const allSubModules = [];
    userModules.forEach((module, moduleIndex) => {
      if (module.SubModulos && Array.isArray(module.SubModulos)) {
        module.SubModulos.forEach((subModule, subIndex) => {
          allSubModules.push({
            ...subModule,
            ModuleDescription: module.Descripcion,
            IdModulo: module.IdModulo
          });
        });
      }
    });
    return allSubModules;
  };

  const hasActionPermission = (subModuleId, action) => {
    if (user?.esAdmin || user?.isAdmin) {
      return true;
    }
    const availableSubModules = getAvailableSubModules();
    const subModule = availableSubModules.find(sub => {
      const match = sub.IdSubModulo === subModuleId;
      return match;
    });
    
    if (!subModule) {
      return false;
    }
    
    if (!subModule.ACCION) {
      return false;
    }
    
    if (!Array.isArray(subModule.ACCION)) {
      return false;
    }
    
    const hasPermission = subModule.ACCION.includes(action);
    return hasPermission;
  };

  const hasSubModuleAccess = (subModuleId) => {
    if (user?.esAdmin || user?.isAdmin) {
      return true;
    }
    
    return userModules.some(module => 
      module.SubModulos?.some(subModule => 
        subModule.IdSubModulo === subModuleId
      )
    );
  };

  const hasModuleAccess = (moduleId) => {
    if (user?.esAdmin || user?.isAdmin) {
      return true;
    }
    return userModules.some(module => module.IdModulo === moduleId);
  };

  const getSubModuleActions = (subModuleId) => {
    if (user?.esAdmin || user?.isAdmin) {
      return ['LISTAR', 'AGREGAR', 'MODIFICAR', 'ELIMINAR'];
    }
    const availableSubModules = getAvailableSubModules();
    const subModule = availableSubModules.find(sub => sub.IdSubModulo === subModuleId);
    return subModule?.ACCION || [];
  };

  const hasAnyActionPermission = (subModuleId, actions) => {
    if (user?.esAdmin || user?.isAdmin) {
      return true;
    }
    return actions.some(action => hasActionPermission(subModuleId, action));
  };

  const hasAllActionPermissions = (subModuleId, actions) => {
    if (user?.esAdmin || user?.isAdmin) {
      return true;
    }
    return actions.every(action => hasActionPermission(subModuleId, action));
  };

  const getSubModulesWithActions = () => {
    const subModulesMap = new Map();
    
    userModules.forEach(module => {
      if (module.SubModulos) {
        module.SubModulos.forEach(subModule => {
          const key = `${module.IdModulo}-${subModule.IdSubModulo}`;
          subModulesMap.set(key, {
            moduleId: module.IdModulo,
            moduleDescription: module.Descripcion,
            subModuleId: subModule.IdSubModulo,
            subModuleDescription: subModule.Descripcion,
            actions: subModule.ACCION || []
          });
        });
      }
    });
    return Array.from(subModulesMap.values());
  };

  const checkApiHealth = async () => {
    try {
      const isHealthy = await apiService.utils.healthCheck();
      return isHealthy;
    } catch (error) {
      return false;
    }
  };

  const value = {
    user,
    userModules,
    loading,
    isAuthenticated,
    requiresPasswordChange,
    
    login,
    logout,
    changePassword,
    checkAuthStatus,
    
    hasSubModuleAccess,
    hasModuleAccess,
    getAvailableSubModules,
    hasActionPermission,
    getSubModuleActions,
    hasAnyActionPermission,
    hasAllActionPermissions,
    getSubModulesWithActions,
    isAdmin: user?.esAdmin || user?.isAdmin || false,
    checkApiHealth,
    
    ...(process.env.NODE_ENV === 'development' && {
      _debug: {
        apiToken: configureApi.getAuthToken(),
        portalToken: getCurrentToken(),
        userRaw: user,
        modulesRaw: userModules,
        tokenExpiration: sessionStorage.getItem('tokenExpiration'),
        storage: 'sessionStorage + portal cookies'
      }
    })
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;